import { z } from 'zod';
import { ChannelId } from './common.js';

/**
 * Two read-only answers about a bundle, both computed by the SAME selector
 * that mints.
 *
 * `capacity` answers "can this open right now, and how many whole packs are
 * left". `simulate` answers "what actually comes out, and when does it run
 * dry". Neither writes anything.
 *
 * They exist because stefancoat's answers to both lived in the browser, over
 * a per-slot model its `build-bundle-from-box` endpoint discards before it
 * ever reaches the database. The preview it drew was decorative.
 */

/** Which slot, and what it is short of. */
export const CapacityProblem = z.object({
  slot_index: z.number().int().min(0),
  /** The label the author gave the slot, if any — easier to act on than an index. */
  label: z.string().nullable(),
});

export const BundleCapacity = z.object({
  bundle_id: z.string(),
  channel_id: ChannelId,
  /** Can every slot be filled, on ANY draw order? */
  can_fill: z.boolean(),
  /**
   * `ok`, `slot_empty` (a configuration problem — a slot with no candidate at
   * all) or `insufficient_stock` (a restocking problem). Different fixes, so
   * never collapsed into one.
   */
  reason: z.enum(['ok', 'slot_empty', 'insufficient_stock']),
  /** Collectibles in one pack: the sum of the slot counts. */
  pack_size: z.number().int().min(0),
  /**
   * How many COMPLETE packs the current stock guarantees. `null` is unbounded.
   *
   * Guarantees, not hopes: it is the largest count that survives every draw
   * order, so a luckier run can do better and none can do worse. A capacity
   * report that promised its best case would be the over-promise this whole
   * layer exists to avoid (L11, L12).
   */
  max_complete_packs: z.number().int().min(0).nullable(),
  /**
   * Copies that exist but can never complete a pack.
   *
   * "20 packs left; 43 commons will be stranded." The answer to the question
   * behind "will the last pack be whole".
   */
  stranded_stock: z.number().int().min(0),
  /** Slots with no candidate at all. */
  empty_slots: z.array(CapacityProblem),
  /** Which reward runs dry first, and the slot that wants it. */
  scarcest: z.object({
    slot_index: z.number().int().min(0),
    reward_id: z.string(),
    remaining: z.number().int().min(0),
  }).nullable(),
});

export const SimulateRequest = z.object({
  /** How many opens to attempt. The server caps this. */
  runs: z.number().int().min(1).max(10_000).default(1000),
  /**
   * Fixes the rng, so the same request twice gives the same answer.
   *
   * Offered because "run it again and see if it still looks wrong" is the
   * first thing anybody does with a simulator, and an unreproducible result
   * makes that useless.
   */
  seed: z.number().int().min(0).optional(),
});

/** What came out, and what the configuration said should come out. */
export const SlotOutcome = z.object({
  slot_index: z.number().int().min(0),
  label: z.string().nullable(),
  /** Observed count per reward. */
  by_reward: z.record(z.string(), z.number().int().min(0)),
  /** Configured share per reward, 0-1, AT FULL STOCK — see the note below. */
  expected: z.record(z.string(), z.number().min(0).max(1)),
});

export const SimulationResult = z.object({
  bundle_id: z.string(),
  channel_id: ChannelId,
  /** Opens attempted. Lower than requested when the stock ran out first. */
  runs: z.number().int().min(0),
  /** Complete packs delivered. Never a partial one. */
  packs: z.number().int().min(0),
  items: z.number().int().min(0),
  by_reward: z.record(z.string(), z.number().int().min(0)),
  /** An untiered copy counts under the empty key. */
  by_tier: z.record(z.string(), z.number().int().min(0)),
  slots: z.array(SlotOutcome),
  /**
   * Packs delivered before one could not be filled. `null` if it never
   * happened within the run.
   */
  exhausted_after: z.number().int().min(0).nullable(),
  /**
   * True when `expected` shares are the odds on the FIRST open rather than a
   * constant.
   *
   * A slot weighted by remaining stock re-derives its odds every draw, so the
   * configured share drifts as the run drains. Saying so beats rendering a
   * deviation column that looks like a bug.
   */
  expected_drifts: z.boolean(),
});

export type BundleCapacity = z.infer<typeof BundleCapacity>;
export type SimulateRequest = z.infer<typeof SimulateRequest>;
export type SimulationResult = z.infer<typeof SimulationResult>;
export type SlotOutcome = z.infer<typeof SlotOutcome>;
